"use client"

import { useState, useEffect, Fragment } from "react"
import { Dialog, Transition } from "@headlessui/react"
import {
  X,
  Save,
  RotateCw,
  AlertCircle,
  Receipt,
  DollarSign,
  FileText,
  CreditCard,
  User,
  ArrowDownRight,
  ArrowUpRight,
} from "lucide-react"
import { SearchableSelect, InputCustom } from "../../components"
import expensesService from "../../services/expensesService"
import creditAccountsService from "../../services/creditAccountsService"
import clientsService from "../../services/clientsService"
import toast from "react-hot-toast"

const expenseTypes = [
  { id: "ad account purchase", value: "ad account purchase", name: "Ad Account Purchase" },
  { id: "setup", value: "setup", name: "Setup" },
  { id: "other", value: "other", name: "Other" },
]

const ExpenseModal = ({ isOpen, onClose, expenseId = null, isEditing = false, onSuccess }) => {
  const [loading, setLoading] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [loadingOptions, setLoadingOptions] = useState(false)

  const [userOptions, setUserOptions] = useState([])
  const [selectedClientAccount, setSelectedClientAccount] = useState(null)

  const [formData, setFormData] = useState({
    title: "",
    type: "",
    note: "",
    credit_account: "",
    client: "",
    amount: "",
    otherType: "",
    direction: "debit", // NEW: debit by default
  })

  const [errors, setErrors] = useState({
    title: "",
    type: "",
    credit_account: "",
    client: "",
    amount: "",
    otherType: "",
    direction: "",
  })

  /* ───────────────────────────── Helpers ───────────────────────────── */

  const resetForm = () => {
    setFormData({
      title: "",
      type: "",
      note: "",
      credit_account: "",
      client: "",
      amount: "",
      otherType: "",
      direction: "debit",
    })
    setErrors({
      title: "",
      type: "",
      credit_account: "",
      client: "",
      amount: "",
      otherType: "",
      direction: "",
    })
    setSelectedClientAccount(null)
  }

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }))
    }

    // Reset custom type if changing away from "other"
    if (field === "type" && value !== "other") {
      setFormData((prev) => ({ ...prev, otherType: "" }))
      setErrors((prev) => ({ ...prev, otherType: "" }))
    }

    // When selecting a client (only on create), fetch its credit account
    if (field === "client" && value && !isEditing) {
      fetchClientCreditAccount(value)
    }
  }

  /* ───────────────────── Fetch: Clients & Accounts ──────────────────── */

  const fetchClients = async () => {
    try {
      setLoadingOptions(true)
      const res = await clientsService.searchClients()
      const responseData = res.data

      if (responseData && responseData.statusCode === 200 && Array.isArray(responseData.data)) {
        const options = responseData.data
          .filter((item) => item && item._id)
          .map((item) => ({
            id: item._id,
            value: item._id,
            name: item.reference || item.fullName || "Unknown Client",
          }))
        setUserOptions(options)
      }
    } catch (err) {
      console.error("Failed to fetch clients:", err)
      toast.error(err.response?.data?.message || "Failed to load clients")
    } finally {
      setLoadingOptions(false)
    }
  }

  const fetchClientCreditAccount = async (clientId) => {
    try {
      setLoadingOptions(true)

      const res = await creditAccountsService.searchAccounts(clientId)
      const responseData = res.data

      if (
        responseData &&
        responseData.statusCode === 200 &&
        Array.isArray(responseData.data) &&
        responseData.data.length > 0
      ) {
        const account = responseData.data[0]

        const accountData = {
          id: account._id,
          name: account.account_name || "Unknown Account",
          value: account._id,
          label: `${account.account_name || "Unknown Account"} (${
            account.account_currency || "USD"
          })\nBalance: $${Number(account.balance ?? 0).toFixed(2)}`,
          balance: Number(account.balance || 0),
          currency: account.account_currency || "USD",
          clientName: account.user?.reference || account.user?.fullName || "",
        }

        setFormData((prev) => ({ ...prev, credit_account: account._id }))
        setSelectedClientAccount(accountData)
        setErrors((prev) => ({ ...prev, credit_account: "" }))
      } else {
        // No credit account for this client
        setFormData((prev) => ({ ...prev, credit_account: "" }))
        setSelectedClientAccount(null)
        setErrors((prev) => ({
          ...prev,
          credit_account:
            "No credit account found for this client. You cannot create an asset for this client until a credit account is created.",
        }))
      }
    } catch (err) {
      console.error("Failed to fetch client credit account:", err)
      setFormData((prev) => ({ ...prev, credit_account: "" }))
      setSelectedClientAccount(null)
      setErrors((prev) => ({
        ...prev,
        credit_account: "Failed to load credit account for this client.",
      }))
    } finally {
      setLoadingOptions(false)
    }
  }

  const fetchExpenseData = async (id) => {
    try {
      const res = await expensesService.get(id)
      const responseData = res.data

      if (responseData.data && responseData.statusCode === 200) {
        const exp = responseData.data

        setFormData({
          title: exp.title || "",
          type: exp.type || "",
          note: exp.note || "",
          credit_account: exp.credit_account?._id || "",
          client: exp.client?._id || "",
          amount: exp.amount?.toString() || "",
          otherType: "",
          direction: exp.direction || "debit",
        })

        // populate account for preview
        if (exp.client?._id) {
          fetchClientCreditAccount(exp.client._id)
        }
      }
    } catch (err) {
      console.error("Failed to load expense data:", err)
    }
  }

  /* ───────────────────── Effects: On Open ───────────────────── */

  useEffect(() => {
    if (!isOpen) return

    const init = async () => {
      try {
        setLoading(true)
        await fetchClients()

        if (expenseId && isEditing) {
          await fetchExpenseData(expenseId)
        } else {
          resetForm()
        }
      } catch (err) {
        console.error("Error initializing asset modal:", err)
      } finally {
        setLoading(false)
      }
    }

    init()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, expenseId, isEditing])

  /* ───────────────────────── Validation ───────────────────────── */

  const validateForm = () => {
    const newErrors = {
      title: "",
      type: "",
      credit_account: "",
      client: "",
      amount: "",
      otherType: "",
      direction: "",
    }

    let isValid = true

    if (!formData.title.trim()) {
      newErrors.title = "Title is required"
      isValid = false
    }

    if (!formData.type) {
      newErrors.type = "Asset type is required"
      isValid = false
    }

    if (formData.type === "other" && !formData.otherType.trim()) {
      newErrors.otherType = "Please specify the asset type"
      isValid = false
    }

    if (!formData.client) {
      newErrors.client = "Client is required"
      isValid = false
    }

    if (!formData.credit_account) {
      newErrors.credit_account =
        "No credit account found for selected client. Please select another client or create a credit account first."
      isValid = false
    }

    if (!formData.amount.trim()) {
      newErrors.amount = "Amount is required"
      isValid = false
    } else if (isNaN(Number(formData.amount)) || Number(formData.amount) <= 0) {
      newErrors.amount = "Please enter a valid amount"
      isValid = false
    }

    if (!formData.direction) {
      newErrors.direction = "Please choose if this is Debit or Adjustment"
      isValid = false
    }

    setErrors(newErrors)
    return isValid
  }

  /* ───────────────────────── Submit ───────────────────────── */

  const handleSubmit = async (e) => {
    if (e) e.preventDefault()
    if (!isEditing && !validateForm()) return
    if (isEditing && !formData.note.trim()) {
      // in edit mode we only allow note update, but we can still allow empty
    }

    try {
      setProcessing(true)

      if (isEditing) {
        // NOTE-ONLY UPDATE (matches backend)
        const res = await expensesService.update(expenseId, {
          note: formData.note,
        })

        if (res.data && (res.data.statusCode === 200 || res.data.statusCode === 201)) {
          onSuccess?.()
          onClose()
        }
      } else {
        const apiData = {
          title: formData.title,
          type: formData.type,
          note: formData.note,
          credit_account: formData.credit_account,
          client: formData.client,
          amount: Number.parseFloat(formData.amount),
          otherType: formData.type === "other" ? formData.otherType.trim() : undefined,
          direction: formData.direction || "debit",
        }

        const res = await expensesService.create(apiData)

        if (res.data && (res.data.statusCode === 200 || res.data.statusCode === 201)) {
          onSuccess?.()
          onClose()
        }
      }
    } catch (err) {
      console.error("Asset operation failed:", err)
    } finally {
      setProcessing(false)
    }
  }

  /* ───────────────────── Derived Balance State ───────────────────── */

  const amountNumber =
    formData.amount && !isNaN(Number(formData.amount)) ? Number(formData.amount) : null

  let remainingBalance = null
  let willBeNegative = false

  if (selectedClientAccount && amountNumber !== null) {
    if (formData.direction === "credit") {
      remainingBalance = selectedClientAccount.balance + amountNumber
    } else {
      remainingBalance = selectedClientAccount.balance - amountNumber
      willBeNegative = remainingBalance < 0
    }
  }

  const isCredit = formData.direction === "credit"

  /* ─────────────────────────── Render ─────────────────────────── */

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        {/* Panel Wrapper */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-3xl transform overflow-hidden rounded-xl bg-white dark:bg-neutral-950 shadow-xl transition-all">
                {/* Header */}
                <div className="border-b border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 px-5 py-4">
                  <div className="flex items-center justify-between">
                    <Dialog.Title className="flex items-center gap-2 text-lg font-medium text-neutral-900 dark:text-white">
                      <Receipt className="h-5 w-5 text-neutral-500 dark:text-neutral-400" />
                      {isEditing ? "Update Asset Note" : "Add New Asset"}
                    </Dialog.Title>
                    <button
                      type="button"
                      onClick={onClose}
                      className="rounded-full p-1.5 text-neutral-500 transition-colors hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800"
                      aria-label="Close"
                    >
                      <X size={20} />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <RotateCw
                        size={30}
                        className="animate-spin text-neutral-600 dark:text-neutral-400"
                      />
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      {/* Form Grid */}
                      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        {/* Title */}
                        <div className="space-y-1">
                          <InputCustom
                            label="Asset Title"
                            placeholder="Enter asset title"
                            value={formData.title}
                            onChange={(value) => handleChange("title", value)}
                            icon={
                              <FileText
                                size={16}
                                className="text-neutral-500 dark:text-neutral-400"
                              />
                            }
                            disabled={isEditing}
                          />
                          {errors.title && (
                            <p className="mt-1 flex items-center gap-1 text-xs text-red-500">
                              <AlertCircle size={12} />
                              {errors.title}
                            </p>
                          )}
                        </div>

                        {/* Amount */}
                        <div className="space-y-1">
                          <InputCustom
                            label="Amount"
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            value={formData.amount}
                            onChange={(value) => handleChange("amount", value)}
                            icon={
                              <DollarSign
                                size={16}
                                className="text-neutral-500 dark:text-neutral-400"
                              />
                            }
                            disabled={isEditing}
                          />
                          {errors.amount && (
                            <p className="mt-1 flex items-center gap-1 text-xs text-red-500">
                              <AlertCircle size={12} />
                              {errors.amount}
                            </p>
                          )}
                        </div>

                        {/* Direction (Debit / Credit) */}
                        <div className="space-y-2 md:col-span-2">
                          <label className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                            Flow Type
                          </label>
                          <div className="inline-flex rounded-xl border border-neutral-200 bg-neutral-50 p-1 dark:border-neutral-800 dark:bg-neutral-900/40">
                            <button
                              type="button"
                              onClick={() => !isEditing && handleChange("direction", "debit")}
                              className={`flex items-center gap-2 px-3 py-1.5 text-xs rounded-lg transition-all ${
                                formData.direction === "debit"
                                  ? "bg-red-600 text-white shadow-sm"
                                  : "text-neutral-700 dark:text-neutral-300"
                              } ${isEditing ? "opacity-60 cursor-not-allowed" : ""}`}
                              disabled={isEditing}
                            >
                              <ArrowDownRight className="h-3 w-3" />
                              <span>Debit (Expense)</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => !isEditing && handleChange("direction", "credit")}
                              className={`ml-1 flex items-center gap-2 px-3 py-1.5 text-xs rounded-lg transition-all ${
                                formData.direction === "credit"
                                  ? "bg-green-600 text-white shadow-sm"
                                  : "text-neutral-700 dark:text-neutral-300"
                              } ${isEditing ? "opacity-60 cursor-not-allowed" : ""}`}
                              disabled={isEditing}
                            >
                              <ArrowUpRight className="h-3 w-3" />
                              <span>Credit (Adjustment)</span>
                            </button>
                          </div>
                          {errors.direction && !isEditing && (
                            <p className="mt-1 flex items-center gap-1 text-xs text-red-500">
                              <AlertCircle size={12} />
                              {errors.direction}
                            </p>
                          )}
                        </div>

                        {/* Asset Type + Other */}
                        <div className="space-y-2">
                          <div className="space-y-1">
                            <label className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                              Asset Type
                            </label>
                            <SearchableSelect
                              options={expenseTypes}
                              value={formData.type}
                              onChange={(value) => handleChange("type", value)}
                              placeholder="Select asset type"
                              icon={<FileText size={16} />}
                              disabled={isEditing}
                            />
                            {errors.type && (
                              <p className="mt-1 flex items-center gap-1 text-xs text-red-500">
                                <AlertCircle size={12} />
                                {errors.type}
                              </p>
                            )}
                          </div>

                          {formData.type === "other" && !isEditing && (
                            <div className="space-y-1">
                              <InputCustom
                                label="Specify Asset Type"
                                placeholder="e.g. Training, Software, Tools..."
                                value={formData.otherType}
                                onChange={(value) => handleChange("otherType", value)}
                                icon={
                                  <FileText
                                    size={16}
                                    className="text-neutral-500 dark:text-neutral-400"
                                  />
                                }
                              />
                              {errors.otherType && (
                                <p className="mt-1 flex items-center gap-1 text-xs text-red-500">
                                  <AlertCircle size={12} />
                                  {errors.otherType}
                                </p>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Client */}
                        <div className="space-y-1">
                          <label className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                            Client
                          </label>
                          <SearchableSelect
                            options={userOptions}
                            value={formData.client}
                            onChange={(value) => handleChange("client", value)}
                            placeholder={loadingOptions ? "Loading clients..." : "Select client"}
                            icon={<User size={16} />}
                            disabled={loadingOptions || isEditing}
                          />
                          {errors.client && (
                            <p className="mt-1 flex items-center gap-1 text-xs text-red-500">
                              <AlertCircle size={12} />
                              {errors.client}
                            </p>
                          )}
                        </div>

                        {errors.credit_account && (
                          <p className="mt-1 w-full md:col-span-2 bg-red-600/5 rounded-2xl px-6 py-3 flex items-center gap-4 text-xs text-red-500">
                            <AlertCircle size={16} />
                            {errors.credit_account}
                          </p>
                        )}

                        {/* Client Account Info */}
                        {formData.client && selectedClientAccount && (
                          <div className="space-y-1 md:col-span-2">
                            <label className="mb-2 block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                              Client Credit Account Information
                            </label>
                            <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4 dark:border-neutral-900 dark:bg-neutral-900/30">
                              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                                <div>
                                  <p className="mb-1 text-xs text-neutral-500 dark:text-neutral-400">
                                    Account Name
                                  </p>
                                  <p className="text-sm font-medium text-neutral-900 dark:text-white">
                                    {selectedClientAccount.name}
                                  </p>
                                </div>
                                <div>
                                  <p className="mb-1 text-xs text-neutral-500 dark:text-neutral-400">
                                    Currency
                                  </p>
                                  <p className="text-sm font-medium text-neutral-900 dark:text-white">
                                    {selectedClientAccount.currency}
                                  </p>
                                </div>
                                <div>
                                  <p className="mb-1 text-xs text-neutral-500 dark:text-neutral-400">
                                    Current Balance
                                  </p>
                                  <div className="flex items-center gap-2">
                                    <p
                                      className={`text-sm font-medium ${
                                        selectedClientAccount.balance < 0
                                          ? "text-red-600 dark:text-red-400"
                                          : "text-green-600 dark:text-green-400"
                                      }`}
                                    >
                                      ${selectedClientAccount.balance.toFixed(2)}
                                    </p>
                                    {willBeNegative && (
                                      <span className="rounded px-2 py-1 text-xs text-amber-600 bg-amber-50 dark:bg-amber-900/20">
                                        Balance will be negative
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {amountNumber !== null && amountNumber > 0 && (
                                <div className="mt-4 border-t border-neutral-200 pt-4 dark:border-neutral-800">
                                  <div className="flex items-center justify-between text-sm">
                                    <span className="text-neutral-600 dark:text-neutral-400">
                                      Asset Amount:
                                    </span>
                                    <span
                                      className={`font-medium ${
                                        isCredit
                                          ? "text-green-600 dark:text-green-400"
                                          : "text-red-600 dark:text-red-400"
                                      }`}
                                    >
                                      {isCredit ? "+" : "-"}${amountNumber.toFixed(2)}
                                    </span>
                                  </div>
                                  <div className="mt-1 flex items-center justify-between text-sm">
                                    <span className="text-neutral-600 dark:text-neutral-400">
                                      Remaining Balance:
                                    </span>
                                    <span
                                      className={`font-medium ${
                                        remainingBalance !== null && remainingBalance < 0
                                          ? "text-red-600 dark:text-red-400"
                                          : "text-green-600 dark:text-green-400"
                                      }`}
                                    >
                                      $
                                      {remainingBalance !== null
                                        ? remainingBalance.toFixed(2)
                                        : "--"}
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Selected Credit Account (read-only summary) */}
                        {formData.client && selectedClientAccount && (
                          <div className="w-full space-y-1 md:col-span-2">
                            <label className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                              Selected Credit Account
                            </label>
                            <div className="flex w-full items-start gap-2 rounded-xl border border-neutral-200 bg-neutral-50 p-3 dark:border-neutral-900 dark:bg-neutral-900/30">
                              <CreditCard
                                size={16}
                                className="h-5 w-5 text-neutral-500 dark:text-neutral-400"
                              />
                              <span className="whitespace-pre-line text-sm text-neutral-900 dark:text-white">
                                {selectedClientAccount.label}
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Notes */}
                        <div className="space-y-1 md:col-span-2">
                          <label className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                            Notes
                            {isEditing && (
                              <span className="text-xs text-amber-600 dark:text-amber-400 ml-1">
                                (You can update the note only)
                              </span>
                            )}
                          </label>
                          <textarea
                            value={formData.note}
                            onChange={(e) => handleChange("note", e.target.value)}
                            placeholder="Add any additional notes about this asset"
                            rows={3}
                            className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-600 shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-neutral-900 dark:bg-neutral-900/30 dark:text-neutral-200 dark:disabled:bg-neutral-800"
                          />
                        </div>
                      </div>

                      {/* Info Box */}
                      <div
                        className={`rounded-xl border p-4 ${
                          willBeNegative && !isCredit
                            ? "border-amber-100 bg-amber-50 dark:border-amber-800/30 dark:bg-amber-900/20"
                            : isCredit
                            ? "border-green-100 bg-green-50 dark:border-green-800/30 dark:bg-green-900/20"
                            : "border-blue-100 bg-blue-50 dark:border-blue-800/30 dark:bg-blue-900/20"
                        }`}
                      >
                        <div className="mb-1 flex items-center gap-2">
                          <AlertCircle
                            size={16}
                            className={
                              willBeNegative && !isCredit
                                ? "text-amber-600 dark:text-amber-400"
                                : isCredit
                                ? "text-green-600 dark:text-green-400"
                                : "text-blue-600 dark:text-blue-400"
                            }
                          />
                          <h3
                            className={`text-sm font-medium ${
                              willBeNegative && !isCredit
                                ? "text-amber-700 dark:text-amber-400"
                                : isCredit
                                ? "text-green-700 dark:text-green-400"
                                : "text-blue-700 dark:text-blue-400"
                            }`}
                          >
                            {isEditing
                              ? "Editing note of existing asset"
                              : isCredit
                              ? "This asset will increase the balance (Adjustment)"
                              : willBeNegative
                              ? "This asset will make the balance negative"
                              : "This asset will decrease the balance"}
                          </h3>
                        </div>
                        <p
                          className={`text-xs ${
                            willBeNegative && !isCredit
                              ? "text-amber-700 dark:text-amber-300"
                              : isCredit
                              ? "text-green-700 dark:text-green-300"
                              : "text-blue-600 dark:text-blue-300"
                          }`}
                        >
                          {isEditing
                            ? "You can only edit the note for this asset. All other details (amount, client, type, direction) are locked for audit purposes."
                            : isCredit
                            ? "You are recording a Adjustment (Credit). This will add the amount to the client's credit account balance."
                            : "You are recording a Debit (Expense). This will subtract the amount from the client's credit account balance."}
                        </p>
                      </div>
                    </form>
                  )}
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 border-t border-neutral-200 bg-neutral-50 px-5 py-4 dark:border-neutral-900 dark:bg-neutral-900/30">
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-xl border border-neutral-300 bg-white px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50 dark:border-neutral-600 dark:bg-transparent dark:text-neutral-300 dark:hover:bg-neutral-800"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={processing || loading}
                    className="inline-flex items-center rounded-xl bg-neutral-900 px-4 py-2 text-sm font-medium text-white transition-all duration-200 hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-70 dark:bg-neutral-100 dark:text-black"
                  >
                    {processing ? (
                      <>
                        <RotateCw className="mr-2 h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        {isEditing ? "Update Note" : "Save Asset"}
                      </>
                    )}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default ExpenseModal
